/**
 * RichTextEditor - TipTap WYSIWYG editor with HTML toggle for email campaigns
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write your email content here...',
}: RichTextEditorProps) {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      // SDA-192: StarterKit v3 bundles `link` + `underline`. Disable the bundled
      // pair so the explicitly-configured TiptapLink/Underline below register
      // once — this clears the "Duplicate extension names ['link','underline']"
      // console warnings and the dropped-marks risk on getHTML().
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { HTMLAttributes: { class: 'list-disc pl-4' } },
        orderedList: { HTMLAttributes: { class: 'list-decimal pl-4' } },
        link: false,
        underline: false,
      }),
      Underline,
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary-600 underline' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'w-full min-h-[320px] px-4 py-3 text-sm focus:outline-none',
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  // Sync external value changes. SDA-192: emitUpdate:false so programmatic
  // setContent doesn't re-fire onUpdate → onChange, which (paired with a
  // post-save reload that re-sets `value`) could clobber the user's just-typed
  // content with a stale/lagged read.
  useEffect(() => {
    if (editor && !isHtmlMode && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor, isHtmlMode]);

  // Focus link input when it appears
  useEffect(() => {
    if (showLinkInput && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [showLinkInput]);

  // Close emoji picker on click outside
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showEmojiPicker]);

  const toggleMode = useCallback(() => {
    setIsHtmlMode((prev) => !prev);
  }, []);

  const handleHtmlChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  if (!editor) return null;

  const openLinkInput = () => {
    const existingUrl = editor.getAttributes('link').href || '';
    setLinkUrl(existingUrl);
    setShowLinkInput(true);
  };

  const applyLink = () => {
    if (linkUrl.trim()) {
      const url = linkUrl.trim().startsWith('http') ? linkUrl.trim() : `https://${linkUrl.trim()}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const cancelLink = () => {
    setShowLinkInput(false);
    setLinkUrl('');
    editor.commands.focus();
  };

  const ToolbarBtn = ({
    onClick,
    active,
    children,
    title,
  }: {
    onClick: () => void;
    active: boolean;
    children: React.ReactNode;
    title: string;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`p-1.5 rounded min-w-[28px] flex items-center justify-center transition-colors ${
        active
          ? 'bg-primary-100 text-primary-700'
          : 'hover:bg-warm-200 text-warm-700'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-warm-200 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent relative">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-warm-50 border-b border-warm-200 flex-wrap">
        {!isHtmlMode && (
          <>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive('bold')}
              title="Bold"
            >
              <span className="font-bold text-sm">B</span>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive('italic')}
              title="Italic"
            >
              <span className="italic text-sm">I</span>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive('underline')}
              title="Underline"
            >
              <span className="underline text-sm">U</span>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleStrike().run()}
              active={editor.isActive('strike')}
              title="Strikethrough"
            >
              <span className="line-through text-sm">S</span>
            </ToolbarBtn>
            <div className="w-px h-5 bg-warm-300 mx-1" />
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              active={editor.isActive('bulletList')}
              title="Bullet List"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              active={editor.isActive('orderedList')}
              title="Numbered List"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                <text x="1" y="8" fontSize="6" fill="currentColor" stroke="none">1</text>
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              active={editor.isActive('heading', { level: 2 })}
              title="Heading"
            >
              <span className="font-bold text-xs">H</span>
            </ToolbarBtn>
            <div className="w-px h-5 bg-warm-300 mx-1" />
            <ToolbarBtn
              onClick={openLinkInput}
              active={editor.isActive('link')}
              title="Insert Link"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
            </ToolbarBtn>
            {/* Emoji Picker */}
            <div className="relative" ref={emojiRef}>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(prev => !prev)}
                title="Insert Emoji"
                className={`p-1.5 rounded min-w-[28px] flex items-center justify-center transition-colors ${
                  showEmojiPicker ? 'bg-primary-100 text-primary-700' : 'hover:bg-warm-200 text-warm-700'
                }`}
              >
                <span className="text-sm leading-none">😊</span>
              </button>
              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-warm-200 z-50 w-72">
                  <div className="max-h-56 overflow-y-auto p-2">
                    {[
                      { label: 'Smileys', emojis: ['😀','😃','😄','😁','😊','😍','🥰','😎','🤩','😇','🙂','😉','😋','🤗','🤔','😏'] },
                      { label: 'Hands & People', emojis: ['👍','👏','🙌','💪','🤝','👋','✌️','🤞','👆','👇','👈','👉','🙏','💃','🕺','🧑‍🎤'] },
                      { label: 'Hearts & Symbols', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','💖','💝','✨','⭐','🌟','💫','🔥','✅','❌'] },
                      { label: 'Celebration', emojis: ['🎉','🎊','🥳','🎁','🎈','🏆','🥇','🎯','💎','👑','🌈','🎭','🎬','🎤','🎵','🎶'] },
                      { label: 'Business', emojis: ['📧','📣','📝','✍️','📅','📌','📎','💡','📊','📈','🔔','💰','🎪','🩰','💼','📱'] },
                      { label: 'Arrows & Misc', emojis: ['➡️','⬅️','⬆️','⬇️','↗️','↘️','🔗','💬','👀','🎵','🌍','☀️','🌙','⚡','🌸','🍀'] },
                    ].map(category => (
                      <div key={category.label} className="mb-2">
                        <p className="text-[10px] font-semibold text-warm-400 uppercase tracking-wide px-1 mb-1">{category.label}</p>
                        <div className="grid grid-cols-8 gap-px">
                          {category.emojis.map(emoji => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => {
                                editor.chain().focus().insertContent(emoji).run();
                                setShowEmojiPicker(false);
                              }}
                              className="w-8 h-8 flex items-center justify-center text-xl hover:bg-warm-100 rounded cursor-pointer transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Mode Toggle */}
        <button
          type="button"
          onClick={toggleMode}
          title={isHtmlMode ? 'Switch to Rich Text' : 'Switch to HTML'}
          className={`ml-auto px-2.5 py-1 rounded text-xs font-medium transition-colors ${
            isHtmlMode
              ? 'bg-primary-100 text-primary-700'
              : 'text-warm-500 hover:bg-warm-200 hover:text-warm-700'
          }`}
        >
          {'</> HTML'}
        </button>
      </div>

      {/* Inline Link Input */}
      {showLinkInput && !isHtmlMode && (
        <div className="flex items-center gap-2 px-3 py-2 bg-warm-50 border-b border-warm-200">
          <svg className="w-4 h-4 text-warm-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <input
            ref={linkInputRef}
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelLink(); }
            }}
            placeholder="https://example.com"
            className="flex-1 px-2 py-1 text-sm border border-warm-200 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          <button
            type="button"
            onClick={applyLink}
            className="px-2.5 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors"
          >
            Apply
          </button>
          {editor.isActive('link') && (
            <button
              type="button"
              onClick={removeLink}
              className="px-2.5 py-1 text-xs font-medium text-danger-600 bg-danger-50 rounded hover:bg-danger-100 transition-colors"
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={cancelLink}
            className="p-1 text-warm-400 hover:text-warm-600 transition-colors"
            title="Cancel"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Editor Area */}
      {isHtmlMode ? (
        <textarea
          value={value}
          onChange={handleHtmlChange}
          rows={16}
          className="w-full px-4 py-3 font-mono text-sm resize-y focus:outline-none"
          placeholder="Paste your HTML here..."
        />
      ) : (
        <div className="[&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:leading-snug [&_.ProseMirror_h2]:my-2 [&_.ProseMirror_h3]:text-base [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:leading-snug [&_.ProseMirror_h3]:my-1.5 [&_.ProseMirror_h3]:text-warm-800">
          <EditorContent editor={editor} />
        </div>
      )}
    </div>
  );
}
